import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import articles from '../content/blog.json';
import './BlogPage.css';

interface ArticleContent {
  slug: string;
  blocks: string[];
}

function isArticleContent(value: unknown): value is ArticleContent[] {
  return Array.isArray(value) && value.every((article) =>
    article !== null && typeof article === 'object' &&
    typeof article.slug === 'string' && Array.isArray(article.blocks) &&
    article.blocks.every((block: unknown) => typeof block === 'string')
  );
}

function InlineText({ text }: { text: string }) {
  return <>{text.split(/(\[[^\]]+\]\(https?:\/\/[^)\s]+\)|\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, index) => {
    const link = /^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/.exec(part);
    if (link) return <a key={index} href={link[2]} target="_blank" rel="noopener noreferrer">{link[1]}</a>;
    if (part.startsWith('***') && part.endsWith('***')) {
      return <strong key={index}><em>{part.slice(3, -3)}</em></strong>;
    }
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*')) return <em key={index}>{part.slice(1, -1)}</em>;
    return part;
  })}</>;
}

function BlogPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((entry) => entry.slug === slug);
  const [content, setContent] = useState<ArticleContent[] | null>(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!article) return;
    const controller = new AbortController();
    setError('');
    setContent(null);
    const load = async () => {
      try {
        const response = await fetch('/blog/articles.json', { signal: controller.signal });
        if (!response.ok) throw new Error(`Blog request failed: ${response.status}`);
        const result: unknown = await response.json();
        if (!isArticleContent(result) || !result.some((entry) => entry.slug === article.slug)) {
          throw new Error('Invalid or missing blog article content.');
        }
        if (!controller.signal.aborted) setContent(result);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Unable to load blog article:', err);
        setError('Unable to load this article. Please try again.');
      }
    };
    void load();
    return () => controller.abort();
  }, [article, attempt]);

  if (!slug) {
    return (
      <section className="blog-page">
        <h1>StandLogic Blog</h1>
        <p>Observations, questions, and stories about whitetail deer, technology, and building StandLogic.</p>
        <ol className="blog-article-list">
          {articles.map((entry) => (
            <li key={entry.slug}>
              {entry.series && (
                <span className="blog-series">{entry.series} &middot; Part {entry.part} of {articles.filter((item) => item.series === entry.series).length}</span>
              )}
              <h3><Link to={`/blog/${entry.slug}`}>{entry.title}</Link></h3>
              <p>{entry.description}</p>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  if (!article) {
    return (
      <section className="blog-page">
        <h1>Article not found</h1>
        <p>This article isn't available.</p>
        <Link to="/blog">Back to the blog</Link>
      </section>
    );
  }

  const blocks = content?.find((entry) => entry.slug === article.slug)?.blocks;
  const seriesArticles = article.series ? articles.filter((entry) => entry.series === article.series) : [];
  const seriesIndex = seriesArticles.findIndex((entry) => entry.slug === article.slug);
  const previous = seriesArticles[seriesIndex - 1];
  const next = seriesArticles[seriesIndex + 1];

  return (
    <article className="blog-page">
      <Link to="/blog">All articles</Link>
      {article.series && (
        <p className="blog-series">{article.series} &middot; Part {article.part} of {seriesArticles.length}</p>
      )}
      <h1>{article.title}</h1>
      {error ? (
        <div className="alert alert-error" role="alert">
          <p>{error}</p>
          <button type="button" className="btn btn-primary" onClick={() => setAttempt((value) => value + 1)}>
            Try again
          </button>
        </div>
      ) : !blocks ? <p role="status">Loading article...</p> : (
        <div className="blog-body">
          {blocks.map((block, index) => {
            if (block.startsWith('## ')) return <h2 key={index}>{block.slice(3)}</h2>;
            const image = /^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)$/.exec(block);
            if (image) {
              return (
                <figure className="blog-image" key={index}>
                  <img src={image[2]} alt={image[1]} loading="lazy" />
                  {image[3] && <figcaption>{image[3]}</figcaption>}
                </figure>
              );
            }
            if (block.startsWith('- ')) {
              return <ul key={index}>{block.split(/\r?\n/).map((line, item) => (
                <li key={item}><InlineText text={line.replace(/^- /, '')} /></li>
              ))}</ul>;
            }
            return <p key={index}><InlineText text={block} /></p>;
          })}
        </div>
      )}
      {article.series && <nav className="blog-pagination" aria-label="Series navigation">
        {previous && <Link to={`/blog/${previous.slug}`}>Previous: {previous.title}</Link>}
        {next && <Link to={`/blog/${next.slug}`}>Next: {next.title}</Link>}
      </nav>}
    </article>
  );
}

export default BlogPage;
